import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";

import { apiFetch } from "../shared/api/apiFetch";

const uiConfig = require("../uiConfig");
const { getMessageForFetchError } = require("../shared/utils/apiError");
const { displayEmailOrDash, isPlaceholderEmail } = require("../shared/utils/displayEmail");

function SurveyResponse() {
  const [searchParams] = useSearchParams();
  const serverUrl = uiConfig.serverInfo.serverUrl;

  const account_id = searchParams.get("account_id") || "";
  const survey_id = searchParams.get("survey_id") || "";
  const token = searchParams.get("token") || "";

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [survey, setSurvey] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [invitation, setInvitation] = useState(null);
  const [entryStatus, setEntryStatus] = useState(null);
  const [completionStatus, setCompletionStatus] = useState(null);

  const [answers, setAnswers] = useState({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");
  const scaleScrollRef = useRef(null);

  useEffect(() => {
    if (!account_id || !survey_id || !token) {
      setLoadError("This survey link is missing required information.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError("");
    apiFetch(serverUrl + "/getSurveyForResponse", {
      method: "POST",
      body: JSON.stringify({ account_id, survey_id, token }),
    })
      .then((resp) => {
        if (resp.ok) return resp.json();
        return resp.text().then((t) => Promise.reject(new Error(t)));
      })
      .then((data) => {
        const nextSurvey = data.survey || null;
        const nextInvitation = data.invitation || null;
        const nextQuestions = Array.isArray(data.questions) ? data.questions : [];

        setSurvey(nextSurvey);
        setInvitation(nextInvitation);

        // If this invitation has already been completed, show a thank-you screen
        if (nextInvitation && (nextInvitation.status || "Pending") === "Completed") {
          setEntryStatus({
            icon: "✅",
            title: "You’ve already submitted this survey.",
            message: "Thank you for your feedback.",
          });
          setQuestions([]);
          setCurrentIndex(0);
          setAnswers({});
          return;
        }

        setQuestions(nextQuestions);
        setEntryStatus(null);
        setCurrentIndex(0);
        setAnswers({});
      })
      .catch((err) => {
        const rawMessage = err.message || getMessageForFetchError(err);
        const message = String(rawMessage || "").trim();

        // Show a full-screen state for known survey availability statuses
        if (
          message === "Survey is closed" ||
          message === "Survey has ended" ||
          message === "Survey is not yet open"
        ) {
          let title = message;
          let friendly = message;

          if (message === "Survey is closed" || message === "Survey has ended") {
            title = "This survey is closed.";
            friendly = "Thank you for your interest. This survey is no longer accepting responses.";
          } else if (message === "Survey is not yet open") {
            title = "This survey is not yet open.";
            friendly = "Please check back later once the survey window begins.";
          }

          setEntryStatus({
            icon: "ℹ️",
            title,
            message: friendly,
          });
          setLoadError("");
          return;
        }

        setEntryStatus(null);
        setLoadError(message);
      })
      .finally(() => setLoading(false));
  }, [account_id, survey_id, token, serverUrl]);

  const currentQuestion = useMemo(
    () => (questions && questions.length > 0 ? questions[currentIndex] : null),
    [questions, currentIndex]
  );

  useLayoutEffect(() => {
    const el = scaleScrollRef.current;
    if (!el) return;
    const pinToHighScores = () => {
      el.scrollLeft = el.scrollWidth;
    };
    pinToHighScores();
    const frame = requestAnimationFrame(pinToHighScores);
    return () => cancelAnimationFrame(frame);
  }, [currentQuestion]);

  function isQuestionAnswered(q, answer) {
    if (!q || !answer) return false;
    const type = q.question_type || "single_choice";
    if (type === "multi_choice") {
      const vals = Array.isArray(answer.values) ? answer.values : [];
      return vals.length > 0;
    }
    if (type === "open_ended") {
      return answer.value != null && String(answer.value).trim().length > 0;
    }
    if (type === "rating" || type === "nps") {
      return answer.value != null && answer.value !== "" && !Number.isNaN(Number(answer.value));
    }
    return answer.value != null && String(answer.value).trim().length > 0;
  }

  const allQuestionsAnswered = useMemo(() => {
    if (!questions || questions.length === 0) return false;
    return questions.every((q) => isQuestionAnswered(q, answers[String(q._id)] || {}));
  }, [questions, answers]);

  const total = questions.length;
  const isLastStep = total > 0 && currentIndex === total - 1;

  const currentQuestionAnswered = useMemo(() => {
    if (!currentQuestion) return false;
    return isQuestionAnswered(currentQuestion, answers[String(currentQuestion._id)] || {});
  }, [currentQuestion, answers]);

  function getAnswerFor(questionId) {
    return answers[questionId] || {};
  }

  function updateAnswer(questionId, updater) {
    setAnswers((prev) => {
      const existing = prev[questionId] || {};
      const next = typeof updater === "function" ? updater(existing) : updater;
      return { ...prev, [questionId]: next };
    });
  }

  function handleSingleChange(questionId, value) {
    updateAnswer(questionId, { value });
  }

  function handleMultiToggle(questionId, optionValue, checked) {
    updateAnswer(questionId, (existing) => {
      const set = new Set(Array.isArray(existing.values) ? existing.values : []);
      if (checked) set.add(optionValue);
      else set.delete(optionValue);
      return { ...existing, values: Array.from(set) };
    });
  }

  function handleTextChange(questionId, value) {
    updateAnswer(questionId, { value });
  }

  function handleNext() {
    if (!questions || questions.length === 0) return;
    setCurrentIndex((idx) => Math.min(idx + 1, questions.length - 1));
  }

  function handlePrev() {
    if (!questions || questions.length === 0) return;
    setCurrentIndex((idx) => Math.max(idx - 1, 0));
  }

  function getRatingRange(q) {
    const min =
      q && q.scale_min != null && !Number.isNaN(Number(q.scale_min))
        ? Number(q.scale_min)
        : q.question_type === "nps"
        ? 0
        : 1;
    const max =
      q && q.scale_max != null && !Number.isNaN(Number(q.scale_max))
        ? Number(q.scale_max)
        : q.question_type === "nps"
        ? 10
        : 5;
    const safeMin = Math.min(min, max);
    const safeMax = Math.max(min, max);
    const values = [];
    for (let v = safeMin; v <= safeMax; v += 1) values.push(v);
    return values;
  }

  const LIKERT_LOW_TO_HIGH = [
    "strongly disagree",
    "disagree",
    "neutral",
    "agree",
    "strongly agree",
  ];

  function scaleTone(t) {
    const hue = Math.round(Math.min(1, Math.max(0, t)) * 142);
    return {
      fill: `hsl(${hue}, 62%, 36%)`,
      tint: `hsl(${hue}, 70%, 94%)`,
      border: `hsl(${hue}, 50%, 42%)`,
    };
  }

  function getLikertOptions(q) {
    const stored = Array.isArray(q.options) && q.options.length > 0
      ? q.options
      : ["Strongly agree", "Agree", "Neutral", "Disagree", "Strongly disagree"];
    const rank = (label) => LIKERT_LOW_TO_HIGH.indexOf(String(label || "").trim().toLowerCase());
    if (stored.every((opt) => rank(opt) >= 0)) {
      return [...stored].sort((a, b) => rank(b) - rank(a));
    }
    return stored;
  }

  function renderQuestionContent(q) {
    if (!q) return null;
    const id = String(q._id);
    const answer = getAnswerFor(id);
    const qType = q.question_type || "single_choice";

    const choiceRowStyle = {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      cursor: "pointer",
      fontSize: "14px",
    };
    const choiceInputStyle = { flexShrink: 0 };
    const choiceLabelStyle = {
      flex: 1,
      minWidth: 0,
      textAlign: "left",
      overflowWrap: "anywhere",
    };

    if (qType === "single_choice") {
      const opts = Array.isArray(q.options) && q.options.length > 0 ? q.options : ["Option 1", "Option 2"];
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: "6px", maxWidth: "100%", minWidth: 0 }}>
          {opts.map((opt) => (
            <label key={opt} style={choiceRowStyle}>
              <input
                type="radio"
                name={id}
                value={opt}
                checked={answer.value === opt}
                onChange={() => handleSingleChange(id, opt)}
                style={choiceInputStyle}
              />
              <span style={choiceLabelStyle}>{opt}</span>
            </label>
          ))}
        </div>
      );
    }

    if (qType === "multi_choice") {
      const opts = Array.isArray(q.options) && q.options.length > 0 ? q.options : ["Option 1", "Option 2"];
      const selected = new Set(Array.isArray(answer.values) ? answer.values : []);
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: "6px", maxWidth: "100%", minWidth: 0 }}>
          {opts.map((opt) => (
            <label key={opt} style={choiceRowStyle}>
              <input
                type="checkbox"
                value={opt}
                checked={selected.has(opt)}
                onChange={(e) => handleMultiToggle(id, opt, e.target.checked)}
                style={choiceInputStyle}
              />
              <span style={choiceLabelStyle}>{opt}</span>
            </label>
          ))}
        </div>
      );
    }

    if (qType === "rating" || qType === "nps") {
      const values = getRatingRange(q);
      const scaleMin = values.length ? Math.min(...values) : 1;
      const scaleMax = values.length ? Math.max(...values) : 5;
      const leftLabel = qType === "nps" ? "Not at all likely" : "Very Dissatisfied";
      const rightLabel = qType === "nps" ? "Extremely likely" : "Very Satisfied";
      const ratingTone = (v) => {
        const span = scaleMax - scaleMin;
        return scaleTone(span === 0 ? 1 : (v - scaleMin) / span);
      };
      return (
        <div style={{ marginTop: "4px", maxWidth: "100%", minWidth: 0 }}>
          <div
            ref={scaleScrollRef}
            style={{
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
              maxWidth: "100%",
            }}
          >
            <div style={{ display: "flex", gap: "6px", width: "max-content", minWidth: "100%" }}>
              {values.map((v) => {
                const selected = answer.value === v;
                const tone = ratingTone(v);
                const endpointLabel = v === scaleMax ? rightLabel : v === scaleMin ? leftLabel : "";
                return (
                  <button
                    key={v}
                    type="button"
                    aria-label={endpointLabel ? `${v}, ${endpointLabel}` : String(v)}
                    onClick={() => handleSingleChange(id, v)}
                    style={{
                      flex: "1 0 32px",
                      minWidth: "32px",
                      boxSizing: "border-box",
                      padding: "6px 0",
                      borderRadius: "4px",
                      border: `1px solid ${tone.border}`,
                      background: selected ? tone.fill : tone.tint,
                      color: selected ? "#fff" : "#1f2937",
                      cursor: "pointer",
                      fontSize: "13px",
                      fontWeight: selected ? 600 : 400,
                    }}
                  >
                    {v}
                  </button>
                );
              })}
            </div>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: "12px",
              marginTop: "6px",
              width: "100%",
              fontSize: "12px",
              color: "var(--sentiment-label, #1f2937)",
            }}
          >
            <span style={{ whiteSpace: "nowrap" }}>{leftLabel}</span>
            <span style={{ marginLeft: "auto", textAlign: "right", whiteSpace: "nowrap" }}>{rightLabel}</span>
          </div>
        </div>
      );
    }

    if (qType === "likert") {
      const opts = getLikertOptions(q);
      const rank = (label) => LIKERT_LOW_TO_HIGH.indexOf(String(label || "").trim().toLowerCase());
      const rankSpan = LIKERT_LOW_TO_HIGH.length - 1;
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "8px",
            marginTop: "4px",
            maxWidth: "100%",
          }}
        >
          {opts.map((opt, idx) => {
            const selected = answer.value === opt;
            const known = rank(opt);
            const t = known >= 0 ? known / rankSpan : opts.length <= 1 ? 1 : (opts.length - 1 - idx) / (opts.length - 1);
            const tone = scaleTone(t);
            return (
              <button
                key={opt}
                type="button"
                onClick={() => handleSingleChange(id, opt)}
                style={{
                  padding: "8px 12px",
                  borderRadius: "4px",
                  border: `1px solid ${tone.border}`,
                  background: selected ? tone.fill : tone.tint,
                  color: selected ? "#fff" : "#1f2937",
                  cursor: "pointer",
                  fontSize: "13px",
                  lineHeight: 1.25,
                  fontWeight: selected ? 600 : 400,
                  textAlign: "left",
                  maxWidth: "100%",
                }}
              >
                {opt}
              </button>
            );
          })}
        </div>
      );
    }

    // open_ended
    return (
      <textarea
        rows={4}
        value={answer.value || ""}
        onChange={(e) => handleTextChange(id, e.target.value)}
        style={{
          width: "100%",
          maxWidth: "100%",
          padding: "6px 8px",
          fontSize: "14px",
          borderRadius: "4px",
          border: "1px solid #ced4da",
          resize: "vertical",
          boxSizing: "border-box",
        }}
        placeholder="Type your answer here…"
      />
    );
  }

  function handleSubmit(e) {
    e && e.preventDefault();
    if (!survey || !questions || questions.length === 0) return;
    setSubmitting(true);
    setSubmitMessage("");

    const payloadAnswers = questions.map((q) => {
      const id = String(q._id);
      const a = answers[id] || {};
      const type = q.question_type || "single_choice";
      if (type === "multi_choice") {
        return { question_id: id, values: Array.isArray(a.values) ? a.values : [] };
      }
      return { question_id: id, value: a.value };
    });

    const hasAnyAnswer = payloadAnswers.some((a) => {
      if (Array.isArray(a.values) && a.values.some((v) => v != null && String(v).trim() !== "")) {
        return true;
      }
      return a.value != null && String(a.value).trim() !== "";
    });
    if (!hasAnyAnswer) {
      setSubmitMessage("Please answer at least one question before submitting.");
      setSubmitting(false);
      return;
    }

    apiFetch(serverUrl + "/submitSurveyResponse", {
      method: "POST",
      body: JSON.stringify({ account_id, survey_id, token, answers: payloadAnswers }),
    })
      .then((resp) => {
        if (resp.ok) return resp.json();
        return resp.text().then((t) => Promise.reject(new Error(t)));
      })
      .then((data) => {
        const submittedAt = new Date();
        setCompletionStatus({
          icon: "✅",
          title: "Thank you for your feedback!",
          message: "Your response has been recorded.",
          submittedAt,
        });
        setSubmitMessage("");
      })
      .catch((err) => {
        const raw = err.message || "Unable to submit survey.";
        const message = String(raw || "").trim();

        // If the server says this link was already submitted, show the completion state
        if (message === "Survey already submitted for this link") {
          const submittedAt = new Date();
          setCompletionStatus({
            icon: "✅",
            title: "Thank you for your feedback!",
            message: "Your response has been recorded.",
            submittedAt,
          });
          setSubmitMessage("");
          return;
        }

        setSubmitMessage(message || "Unable to submit survey.");
      })
      .finally(() => setSubmitting(false));
  }

  if (loading) {
    return (
      <div
        style={{
          maxWidth: "640px",
          margin: "40px auto",
          padding: "16px",
          fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        }}
      >
        <p>Loading survey…</p>
      </div>
    );
  }

  if (completionStatus) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "16px",
          fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          background: "#f8f9fa",
        }}
      >
        <div
          style={{
            maxWidth: "480px",
            width: "100%",
            textAlign: "center",
            borderRadius: "12px",
            border: "1px solid #dee2e6",
            padding: "24px 20px",
            background: "#ffffff",
            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.04)",
          }}
        >
          {completionStatus.icon && (
            <div style={{ fontSize: "32px", marginBottom: "12px" }}>{completionStatus.icon}</div>
          )}
          <h1 style={{ fontSize: "1.4rem", margin: "0 0 8px 0", color: "#212529" }}>
            {completionStatus.title}
          </h1>
          <p style={{ margin: "0 0 4px 0", color: "#495057", fontSize: "14px" }}>
            {completionStatus.message}
          </p>
          {completionStatus.submittedAt && (
            <p style={{ margin: "8px 0 0 0", color: "#6c757d", fontSize: "13px" }}>
              Submitted on: {dayjs(completionStatus.submittedAt).format("MMM D, h:mm A")}
            </p>
          )}
          {survey?.name && (
            <p style={{ margin: "12px 0 0 0", fontSize: "12px", color: "#6c757d" }}>
              Survey: {survey.name}
            </p>
          )}
          {invitation?.email && !isPlaceholderEmail(invitation.email) && (
            <p style={{ margin: "4px 0 0 0", fontSize: "12px", color: "#6c757d" }}>
              Invited as: {displayEmailOrDash(invitation.email)}
            </p>
          )}
        </div>
      </div>
    );
  }

  if (entryStatus) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "16px",
          fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          background: "#f8f9fa",
        }}
      >
        <div
          style={{
            maxWidth: "480px",
            width: "100%",
            textAlign: "center",
            borderRadius: "12px",
            border: "1px solid #dee2e6",
            padding: "24px 20px",
            background: "#ffffff",
            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.04)",
          }}
        >
          {entryStatus.icon && (
            <div style={{ fontSize: "32px", marginBottom: "12px" }}>{entryStatus.icon}</div>
          )}
          <h1 style={{ fontSize: "1.4rem", margin: "0 0 8px 0", color: "#212529" }}>
            {entryStatus.title}
          </h1>
          <p style={{ margin: "0 0 4px 0", color: "#495057", fontSize: "14px" }}>
            {entryStatus.message}
          </p>
          {survey?.name && (
            <p style={{ margin: "12px 0 0 0", fontSize: "12px", color: "#6c757d" }}>
              Survey: {survey.name}
            </p>
          )}
          {invitation?.email && !isPlaceholderEmail(invitation.email) && (
            <p style={{ margin: "4px 0 0 0", fontSize: "12px", color: "#6c757d" }}>
              Invited as: {displayEmailOrDash(invitation.email)}
            </p>
          )}
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div
        style={{
          maxWidth: "640px",
          margin: "40px auto",
          padding: "16px",
          fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        }}
      >
        <h2 style={{ color: "#842029", marginBottom: "8px", fontSize: "1.2rem" }}>Survey error</h2>
        <p style={{ color: "#212529" }}>{loadError}</p>
      </div>
    );
  }

  if (!survey) {
    return (
      <div
        style={{
          maxWidth: "640px",
          margin: "40px auto",
          padding: "16px",
          fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        }}
      >
        <p>Survey not found.</p>
      </div>
    );
  }

  const step = total > 0 ? currentIndex + 1 : 0;

  return (
    <div
      style={{
        maxWidth: "720px",
        width: "100%",
        boxSizing: "border-box",
        margin: "24px auto",
        padding: "16px",
        overflowX: "hidden",
        fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}
    >
      <div
        style={{
          borderRadius: "12px",
          border: "1px solid #dee2e6",
          padding: "16px 18px",
          background: "#f8f9fa",
          marginBottom: "16px",
          maxWidth: "100%",
          boxSizing: "border-box",
          overflowWrap: "anywhere",
        }}
      >
        <h1 style={{ margin: "0 0 6px 0", fontSize: "1.4rem", color: "#212529" }}>
          {survey.name || "Survey"}
        </h1>
        {survey.description && (
          <p style={{ margin: "4px 0 0 0", fontSize: "13px", color: "#495057" }}>
            {survey.description}
          </p>
        )}
        {survey.start_date && survey.end_date && (
          <p style={{ margin: "6px 0 0 0", fontSize: "12px", color: "#6c757d" }}>
            Open from{" "}
            {dayjs(survey.start_date).format("MMM D, YYYY")} to{" "}
            {dayjs(survey.end_date).format("MMM D, YYYY")}.
          </p>
        )}
        {invitation?.email && !isPlaceholderEmail(invitation.email) && (
          <p style={{ margin: "6px 0 0 0", fontSize: "12px", color: "#6c757d" }}>
            Invited as: {displayEmailOrDash(invitation.email)}
          </p>
        )}
      </div>

      {total === 0 ? (
        <p style={{ fontSize: "14px", color: "#6c757d" }}>
          This survey does not have any questions yet.
        </p>
      ) : (
        <form onSubmit={handleSubmit}>
          <div
            style={{
              borderRadius: "12px",
              border: "1px solid #dee2e6",
              padding: "16px 18px",
              background: "#fff",
              maxWidth: "100%",
              minWidth: 0,
              boxSizing: "border-box",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                marginBottom: "8px",
              }}
            >
              <div
                style={{
                  fontSize: "12px",
                  color: "#6c757d",
                }}
              >
                Question {step} of {total}
              </div>
            </div>
            <div
              style={{
                fontSize: "1rem",
                fontWeight: 600,
                marginBottom: "10px",
                color: "#212529",
                overflowWrap: "anywhere",
              }}
            >
              {currentQuestion?.question_text || "Question"}
            </div>
            {renderQuestionContent(currentQuestion)}
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: "12px",
              gap: "8px",
              flexWrap: "wrap",
            }}
          >
            <div style={{ display: "flex", gap: "6px" }}>
              <button
                type="button"
                onClick={handlePrev}
                disabled={currentIndex === 0}
                style={{
                  padding: "6px 12px",
                  fontSize: "13px",
                  borderRadius: "4px",
                  border: "1px solid #dee2e6",
                  background: currentIndex === 0 ? "#f8f9fa" : "#fff",
                  color: "#212529",
                  cursor: currentIndex === 0 ? "not-allowed" : "pointer",
                }}
              >
                Previous
              </button>
              {!isLastStep && (
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={!currentQuestionAnswered}
                  style={{
                    padding: "6px 12px",
                    fontSize: "13px",
                    borderRadius: "4px",
                    border: "1px solid #dee2e6",
                    background: !currentQuestionAnswered ? "#f8f9fa" : "#fff",
                    color: "#212529",
                    cursor: !currentQuestionAnswered ? "not-allowed" : "pointer",
                  }}
                >
                  Next
                </button>
              )}
            </div>
            {isLastStep && (
              <button
                type="submit"
                disabled={submitting || !allQuestionsAnswered}
                style={{
                  padding: "6px 16px",
                  fontSize: "13px",
                  borderRadius: "4px",
                  border: "none",
                  background: submitting || !allQuestionsAnswered ? "#adb5bd" : "#0d6efd",
                  color: "#fff",
                  cursor: submitting || !allQuestionsAnswered ? "not-allowed" : "pointer",
                  minWidth: "120px",
                }}
              >
                {submitting ? "Submitting…" : "Submit survey"}
              </button>
            )}
          </div>
        </form>
      )}

      {submitMessage && (
        <div
          style={{
            marginTop: "16px",
            padding: "10px 12px",
            borderRadius: "6px",
            border: "1px solid #dee2e6",
            background: "#f8f9fa",
            fontSize: "13px",
            color: "#212529",
          }}
        >
          {submitMessage}
        </div>
      )}
    </div>
  );
}

export default SurveyResponse;

